export const DEFAULT_TIMEZONE = 'America/New_York';

const CLIENT_FIELDS = ['shipper_id', 'carrier_id', 'middle_mile_carrier_id'];
const JSON_FIELDS = ['params', 'body'];
const CRON_FIELD = /^[0-9A-Za-z*/,-]+$/;

export const emptyDraft = () => ({
    url: '',
    crontab: '',
    timezone: DEFAULT_TIMEZONE,
    shipper_id: '',
    carrier_id: '',
    middle_mile_carrier_id: '',
    params: '{}',
    body: '{}',
});

export const draftFromAutomation = (automation) => ({
    url: automation.url || '',
    crontab: automation.crontab || '',
    timezone: automation.timezone || '',
    ...Object.fromEntries(CLIENT_FIELDS.map((field) => [field, automation[field] || ''])),
    ...Object.fromEntries(JSON_FIELDS.map((field) => [field, JSON.stringify(automation[field] || {}, null, 2)])),
});

const normalizeCrontab = (crontab) => crontab.trim().split(/\s+/).join(' ');

const normalizeUrl = (url) => url.trim().replace(/^\/+/, '');

export const isValidCrontab = (crontab) => {
    const fields = crontab.trim().split(/\s+/);

    return fields.length === 5 && fields.every((field) => CRON_FIELD.test(field));
};

export const isValidTimezone = (timezone) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone });
        return true;
    } catch {
        return false;
    }
};

export const parseJsonObject = (text) => {
    if (!text.trim()) return { value: {} };

    let value;
    try {
        value = JSON.parse(text);
    } catch {
        return { error: 'Must be valid JSON' };
    }

    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return { error: 'Must be a JSON object' };
    }

    return { value };
};

export const validateDraft = (draft) => {
    const errors = {};

    if (!draft.url.trim()) {
        errors.url = 'Required';
    } else if (draft.url.includes('://')) {
        errors.url = 'Use the path after the functions URL, e.g. v2-lib-automations-endpoints/…';
    }

    if (!draft.crontab.trim()) {
        errors.crontab = 'Required';
    } else if (!isValidCrontab(draft.crontab)) {
        errors.crontab = 'Use 5 fields: minute hour day-of-month month day-of-week';
    }

    if (!draft.timezone) {
        errors.timezone = 'Required';
    } else if (!isValidTimezone(draft.timezone)) {
        errors.timezone = 'Unknown timezone';
    }

    JSON_FIELDS.forEach((field) => {
        const { error } = parseJsonObject(draft[field]);
        if (error) errors[field] = error;
    });

    return errors;
};

// Assumes validateDraft passed.
export const toAutomationInput = (draft) => ({
    url: normalizeUrl(draft.url),
    crontab: normalizeCrontab(draft.crontab),
    timezone: draft.timezone,
    ...Object.fromEntries(CLIENT_FIELDS.map((field) => [field, draft[field] || null])),
    ...Object.fromEntries(JSON_FIELDS.map((field) => [field, parseJsonObject(draft[field]).value])),
});

// Row values are cached per data reference, so names must live on the rows rather than in column closures.
export const withClientNames = (automations, names) =>
    automations.map((automation) => ({
        ...automation,
        ...Object.fromEntries(
            CLIENT_FIELDS.map((field) => [
                `${field}_name`,
                automation[field] ? names[automation[field]] || automation[field] : '',
            ])
        ),
    }));

const NONE_OPTION = { value: '', label: 'None' };

const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

export const clientOptions = (clients = []) => {
    const names = {};
    const shippers = [];
    const carriers = [];

    clients.forEach((client) => {
        const option = { value: client.client_id, label: client.business_name || client.client_id };
        names[option.value] = option.label;

        if (client.user?.roles?.SHIPPER) shippers.push(option);
        if (client.user?.roles?.CARRIER) carriers.push(option);
    });

    return {
        names,
        shippers: [NONE_OPTION, ...shippers.sort(byLabel)],
        carriers: [NONE_OPTION, ...carriers.sort(byLabel)],
    };
};
